import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import Select from "../../../../components/input/select/Select";

import { useCreatorAlbums } from "../hooks/useAlbums";

import "./CreatorAlbumForm.css";
import "./CreatorEditAlbumForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

export default function CreatorEditAlbumForm({
  username,
  album,
  onClose,
  onRefresh,
}) {
  const { loading, error, getCreatorAlbumEditData, updateCreatorAlbum } =
    useCreatorAlbums(username);

  const albumId = Number(album?.albumId);

  const [title, setTitle] = useState(album?.title || "");
  const [caption, setCaption] = useState(album?.caption || "");
  const [visibility, setVisibility] = useState(album?.visibility || "private");

  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [formError, setFormError] = useState("");

  const isBusy = loading || saving || initializing;

  const validVisibility = VISIBILITY_OPTIONS.some(
    (option) => option.value === visibility,
  );

  const canSubmit = Boolean(title.trim()) && validVisibility && !isBusy;

  useEffect(() => {
    if (!username || !Number.isInteger(albumId) || albumId <= 0) {
      setInitializing(false);
      return;
    }

    let active = true;

    const loadAlbum = async () => {
      setInitializing(true);
      setFormError("");

      try {
        const result = await getCreatorAlbumEditData(albumId);

        if (!active) return;

        if (result === false || result?.success === false) {
          setFormError("Unable to load album details.");
          return;
        }

        const currentAlbum = result?.album || album || {};

        setTitle(currentAlbum.title || "");
        setCaption(currentAlbum.caption || "");

        const currentVisibility = String(
          currentAlbum.visibility || "private",
        ).toLowerCase();

        setVisibility(
          VISIBILITY_OPTIONS.some(
            (option) => option.value === currentVisibility,
          )
            ? currentVisibility
            : "private",
        );
      } catch (loadError) {
        if (active) {
          console.error("Unable to load album edit data:", loadError);
          setFormError("Unable to load album details.");
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    loadAlbum();

    return () => {
      active = false;
    };
  }, [username, albumId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || saving) return;

      event.preventDefault();
      onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [saving, onClose]);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!canSubmit) return;

    if (!username || !Number.isInteger(albumId) || albumId <= 0) {
      setFormError("Album information is missing.");
      return;
    }

    try {
      setSaving(true);
      setFormError("");

      const result = await updateCreatorAlbum({
        albumId,
        title: title.trim(),
        caption: caption.trim(),
        visibility,
      });

      if (result === false || result?.success === false) {
        setFormError(result?.message || "Unable to save album changes.");
        return;
      }

      await onRefresh?.();
      onClose?.();
    } catch (saveError) {
      console.error("Unable to update album:", saveError);

      setFormError(
        saveError?.response?.data?.message || "Unable to save album changes.",
      );
    } finally {
      setSaving(false);
    }
  };

  const errorMessage =
    formError || (typeof error === "string" ? error : error?.message || "");

  return createPortal(
    <div
      className="creator-album-form creator-album-form--edit"
      role="dialog"
      aria-modal="true"
      aria-label="Edit Album Details"
    >
      <header className="creator-album-form__header">
        <div className="creator-album-form__header-info">
          <h2>Edit Album Details</h2>
          <p>Update your album information and visibility.</p>
        </div>

        <button
          type="button"
          className="creator-album-form__close"
          onClick={handleClose}
          disabled={saving}
          aria-label="Close edit album form"
        >
          <X size={22} />
        </button>
      </header>

      <div className="creator-album-form__workspace">
        <main className="creator-album-form__main">
          <div className="creator-album-form__edit-content">
            <div className="creator-album-form__edit-header">
              <h3>Album Information</h3>
              <p>Make changes to your album details below.</p>
            </div>

            <form
              id="creator-edit-album-form"
              className="creator-album-form__fields"
              onSubmit={handleSubmit}
            >
              <TextField
                label="Title"
                name="title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Give your album a title"
                maxLength={60}
                required
                disabled={isBusy}
              />

              <TextArea
                label="Caption"
                name="caption"
                value={caption}
                onChange={(event) => setCaption(event.target.value)}
                placeholder="Tell viewers about this album"
                maxLength={2000}
                maxHeight={180}
                disabled={isBusy}
              />

              <Select
                label="Visibility"
                name="visibility"
                value={visibility}
                onChange={(event) => setVisibility(event.target.value)}
                items={VISIBILITY_OPTIONS}
                required
                disabled={isBusy}
              />
            </form>
          </div>
        </main>
      </div>

      <footer className="creator-album-form__footer">
        <div className="creator-album-form__footer-status">
          {errorMessage && (
            <span className="creator-album-form__error" role="alert">
              {errorMessage}
            </span>
          )}

          {!errorMessage && initializing && (
            <span>Loading album details...</span>
          )}

          {!errorMessage && saving && <span>Saving album changes...</span>}
        </div>

        <div className="creator-album-form__actions">
          <Button
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={saving}
          />

          <Button
            value={saving ? "Saving..." : "Save Changes"}
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </div>,
    document.body,
  );
}
